// src/hooks/useCsvParser.ts

import { useState, useCallback } from 'react';
import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';
import { Transaction } from '../components/TransactionUploader/types';

export function useCsvParser(onDataParsed: (txs: Transaction[]) => void) {
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = useCallback((file: File | null) => {
    if (!file) {
      setError(null);
      return;
    }

    setError(null);
    const name = file.name.toLowerCase();

    // --- CSV branch ---
    if (name.endsWith('.csv')) {
      Papa.parse<Transaction>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: ParseResult<Transaction>) => {
          const data = results.data
            .map(row => {
              // trim every field
              Object.keys(row).forEach(k => {
                row[k] = row[k]?.trim() ?? '';
              });
              return row;
            })
            // remove rows without Avere/Dare
            .filter(r => r['Avere'] || r['Dare']);
          onDataParsed(data);
        },
        error: () => setError('Errore parsing CSV'),
      });

    // --- XLSX branch ---
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target?.result as ArrayBuffer, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];

          // 1) Read every row as an array (no header)
          const rawRows: any[][] = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: '',
          }) as any[][];

          // 2) Find the header row that contains Avere and Dare
          const headerIndex = rawRows.findIndex(row =>
            row.includes('Avere') && row.includes('Dare')
          );
          if (headerIndex < 0) {
            setError("Intestazioni 'Avere' e 'Dare' non trovate");
            return;
          }

          // 3) Extract headers and data rows
          const headerRow = rawRows[headerIndex].map(String);
          const dataRows = rawRows.slice(headerIndex + 1);

          // 4) Build Transaction[] and filter
          const data: Transaction[] = dataRows
            .map(row => {
              const tx: Transaction = {};
              headerRow.forEach((col, i) => {
                tx[col] = String(row[i] ?? '').trim();
              });
              return tx;
            })
            .filter(r => r['Avere'] || r['Dare']);

          onDataParsed(data);
        } catch {
          setError('Errore parsing Excel');
        }
      };
      reader.readAsArrayBuffer(file);

    // --- Unsupported format ---
    } else {
      setError('Formato non supportato (usare .csv o .xlsx)');
    }
  }, [onDataParsed]);

  return { error, handleFileUpload };
}
